<template>
  <controller>
    <main class="parameterization-page">
      <header class="parameterization-header">
        <div>
          <router-link v-if="!hasEvaluationPermission" to="/appscripts/sgu">&larr; Voltar ao dashboard</router-link>
          <h1>{{ canManage ? 'Parametrização do SGU' : 'Requisitos do SGU' }}</h1>
          <p>{{ canManage ? 'Gerencie todos os campos da matriz de requisitos usada nas avaliações.' : 'Consulte os requisitos usados nas avaliações.' }}</p>
        </div>
        <nav v-if="canViewDashboard || hasEvaluationPermission || canViewResponses" class="parameterization-actions" aria-label="Navegação do SGU">
          <router-link v-if="canViewDashboard" to="/appscripts/sgu/dashboard">Dashboard</router-link>
          <router-link class="active" to="/appscripts/sgu/parametrizacao" aria-current="page">Requisitos</router-link>
          <router-link v-if="hasEvaluationPermission" :to="evaluationRoute">Avaliação</router-link>
          <router-link v-if="hasDirectorPermission || canViewResponses" :to="reportRoute">Relatórios</router-link>
        </nav>
      </header>

      <form-spinner v-if="loading" />
      <section v-else-if="loadError" class="state-card error" role="alert">
        <p>{{ loadError }}</p>
        <button type="button" @click="loadParameterization">Tentar novamente</button>
      </section>

      <template v-else>
        <nav class="parameterization-tabs" aria-label="Cadastros do SGU">
          <button
            v-for="tab in tabs"
            :key="tab.key"
            type="button"
            :class="{ active: activeTab === tab.key }"
            @click="selectTab(tab.key)"
          >
            <span>{{ tab.label }}</span>
            <b>{{ tab.active }}/{{ tab.total }}</b>
          </button>
        </nav>

        <section :class="['toolbar-card', { 'evaluator-toolbar': hasEvaluationPermission }]">
          <label v-if="!hasEvaluationPermission" class="filter-field">
            <span>Ciclo</span>
            <select v-model="cycleId" @change="loadParameterization">
              <option v-for="cycle in cycles" :key="cycle.id" :value="cycle.id">{{ cycle.code }}</option>
            </select>
          </label>
          <label class="search-field">
            <span>Buscar</span>
            <input v-model.trim="search" type="search" :placeholder="`Buscar em ${pluralLabel.toLowerCase()}...`">
          </label>
          <label v-if="canManage" class="inactive-filter">
            <input v-model="showInactive" type="checkbox">
            <span>Exibir inativos</span>
          </label>
        </section>

        <section class="list-card">
          <div class="list-heading">
            <div>
              <h2>{{ pluralLabel }}</h2>
              <p>{{ filteredItems.length }} registro(s) encontrado(s)</p>
            </div>
            <a v-if="canManage" href="#" class="btn btn-primary" @click.prevent="openCreate">
              + Novo {{ singularLabel.toLowerCase() }}
            </a>
          </div>

          <div v-if="filteredItems.length" class="table-wrap">
            <table v-if="activeTab === 'requirements'" class="requirements-table">
              <thead>
                <tr>
                  <th>Processo</th>
                  <th>Documentos</th>
                  <th class="basic-layer-header">Básico</th>
                  <th class="sustainable-layer-header">Sustentável</th>
                  <th class="high-performance-layer-header">Alta Performance</th>
                  <th v-if="canManage" class="actions-column">Edição</th>
                </tr>
              </thead>
              <tbody v-for="group in requirementGroups" :key="group.key">
                <tr class="mandala-section-row">
                  <td :colspan="canManage ? 6 : 5">
                    <button type="button" class="mandala-section-toggle" :aria-expanded="!isMandalaAreaCollapsed(group.key)" @click="toggleMandalaArea(group.key)">
                      <span>{{ isMandalaAreaCollapsed(group.key) ? '▶' : '▼' }}</span>
                      {{ group.name }}
                      <small>{{ group.items.length }} requisito(s)</small>
                    </button>
                  </td>
                </tr>
                <tr v-for="item in group.items" v-show="!isMandalaAreaCollapsed(group.key)" :key="item.id">
                  <td>
                    <strong class="requirement-title">
                      <span class="requirement-reference">ID-{{ item.referenceId }}</span>
                      <span>{{ item.name }}</span>
                    </strong>
                    <span :class="['nature-pill', natureClass(item.nature)]">{{ item.nature || 'Natureza não informada' }}</span>
                    <span v-if="!item.active" class="status-badge inactive">Inativo</span>
                  </td>
                  <td class="documents-cell">
                    <template v-if="relatedDocuments(item.relatedDocuments).length">
                      <template v-for="(document, index) in relatedDocuments(item.relatedDocuments)" :key="`${item.id}-${index}`">
                        <a v-if="document.url" :href="document.url" target="_blank" rel="noopener noreferrer">{{ document.label }}</a>
                        <span v-else>{{ document.label }}</span>
                        <small v-if="document.reference">{{ document.reference }}</small>
                      </template>
                    </template>
                    <span v-else>—</span>
                  </td>
                  <td class="description-cell basic-layer-cell">{{ item.basicDescription }}</td>
                  <td class="description-cell sustainable-layer-cell">{{ item.sustainableDescription }}</td>
                  <td class="description-cell high-performance-layer-cell">{{ item.highPerformanceDescription || '—' }}</td>
                  <td v-if="canManage" class="row-actions">
                    <a href="#" class="btn btn-primary btn-small" @click.prevent="openEdit(item)">Editar</a>
                    <a v-if="item.active" href="#" class="btn btn-secondary btn-small" @click.prevent="deactivate(item)">Desativar</a>
                  </td>
                </tr>
              </tbody>
            </table>
            <table v-else>
              <thead>
                <tr>
                  <th>Ordem</th>
                  <th>Código / Nome</th>
                  <th>Uso</th>
                  <th>Status</th>
                  <th class="actions-column">Ações</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="item in filteredItems" :key="item.id">
                  <td><span class="order-badge">{{ item.displayOrder }}</span></td>
                  <td>
                    <strong>{{ item.name }}</strong>
                    <small>{{ item.code }}</small>
                    <p v-if="activeTab === 'indicators' && item.description">{{ item.description }}</p>
                  </td>
                  <td>{{ item.requirementsCount }} requisito(s) ativo(s)</td>
                  <td><span :class="['status-badge', { inactive: !item.active }]">{{ item.active ? 'Ativo' : 'Inativo' }}</span></td>
                  <td class="row-actions">
                    <a href="#" class="btn btn-primary btn-small" @click.prevent="openEdit(item)">Editar</a>
                    <a v-if="item.active" href="#" class="btn btn-secondary btn-small" @click.prevent="deactivate(item)">Desativar</a>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
          <div v-else class="empty-state">Nenhum registro corresponde aos filtros informados.</div>
        </section>
      </template>

      <div v-if="canManage && editorOpen" class="editor-overlay" role="presentation" @click.self="closeEditor">
        <form class="editor-modal" @submit.prevent="save">
          <header>
            <div>
              <small>{{ editingId ? 'Editar cadastro' : 'Novo cadastro' }}</small>
              <h2>{{ singularLabel }}</h2>
            </div>
            <button type="button" aria-label="Fechar" :disabled="saving" @click="closeEditor">×</button>
          </header>

          <div class="form-grid">
            <label v-if="activeTab === 'requirements'">
              <span>Ciclo <b>*</b></span>
              <select v-model.number="form.cycleId" required>
                <option v-for="cycle in cycles" :key="cycle.id" :value="cycle.id">{{ cycle.code }}</option>
              </select>
            </label>
            <label v-if="activeTab === 'requirements'">
              <span>ID da matriz <b>*</b></span>
              <input v-model.number="form.referenceId" type="number" min="1" max="65535" required>
            </label>
            <label v-if="activeTab === 'requirements'">
              <span>Peso <b>*</b></span>
              <input v-model.number="form.weight" type="number" min="1" max="999999.99" step="0.01" required>
            </label>
            <label v-if="activeTab === 'requirements'">
              <span>Anexo na autoavaliação <b>*</b></span>
              <select v-model="form.requiresSelfAssessmentAttachment" required>
                <option :value="false">Não exigido</option>
                <option :value="true">Obrigatório</option>
              </select>
            </label>
            <label v-if="activeTab === 'requirements'">
              <span>Área da Mandala <b>*</b></span>
              <select v-model.number="form.mandalaAreaId" required>
                <option value="" disabled>Selecione uma área</option>
                <option v-for="area in selectableMandalaAreas" :key="area.id" :value="area.id">{{ optionLabel(area) }}</option>
              </select>
            </label>
            <label v-if="activeTab === 'requirements'">
              <span>Natureza <b>*</b></span>
              <select v-model.number="form.natureId" required>
                <option value="" disabled>Selecione uma natureza</option>
                <option v-for="nature in selectableNatures" :key="nature.id" :value="nature.id">{{ optionLabel(nature) }}</option>
              </select>
            </label>
            <label v-if="activeTab === 'requirements'">
              <span>Área responsável</span>
              <select v-model="form.areaId">
                <option :value="null">Sem área responsável</option>
                <option v-for="area in selectableAreas" :key="area.id" :value="area.id">{{ optionLabel(area) }}</option>
              </select>
            </label>
            <label v-if="activeTab === 'requirements'">
              <span>Indicador</span>
              <select v-model="form.indicatorId">
                <option :value="null">Sem indicador</option>
                <option v-for="indicator in selectableIndicators" :key="indicator.id" :value="indicator.id">{{ optionLabel(indicator) }}</option>
              </select>
            </label>
            <label>
              <span>Código <b>*</b></span>
              <input v-model.trim="form.code" maxlength="120" required placeholder="Ex.: comercial-escolas-parceiras">
            </label>
            <label>
              <span>Nome <b>*</b></span>
              <input v-model.trim="form.name" maxlength="255" required>
            </label>
            <label>
              <span>Ordem <b>*</b></span>
              <input v-model.number="form.displayOrder" type="number" min="0" max="65535" required>
            </label>
            <label>
              <span>Status <b>*</b></span>
              <select v-model="form.active">
                <option :value="true">Ativo</option>
                <option :value="false">Inativo</option>
              </select>
            </label>
          </div>

          <label v-if="activeTab === 'indicators'" class="full-field">
            <span>Descrição</span>
            <textarea v-model.trim="form.description" rows="4" maxlength="5000" />
          </label>

          <section v-if="activeTab === 'requirements'" class="document-editor">
            <div class="document-editor-heading">
              <div>
                <strong>Documentos relacionados</strong>
                <small>Personalize o nome exibido e informe o link ou uma referência textual.</small>
              </div>
              <a href="#" class="btn btn-secondary btn-small" @click.prevent="addDocument">+ Adicionar documento</a>
            </div>
            <div v-if="documentEntries.length" class="document-editor-list">
              <div v-for="(document, index) in documentEntries" :key="document.key" class="document-editor-row">
                <input-control v-model="document.label" label="Nome do documento" maxlength="255" :placeholder="`Documento ${index + 1}`" />
                <input-control v-model="document.destination" label="Link ou referência" maxlength="5000" placeholder="https://... ou referência interna" />
                <a href="#" class="btn btn-secondary btn-small" @click.prevent="removeDocument(index)">Remover</a>
              </div>
            </div>
            <p v-else>Nenhum documento relacionado.</p>
          </section>

          <div v-if="activeTab === 'requirements'" class="layer-fields">
            <label>
              <span>Descrição Básica <b>*</b></span>
              <textarea v-model.trim="form.basicDescription" rows="5" maxlength="10000" required />
            </label>
            <label>
              <span>Descrição Sustentável <b>*</b></span>
              <textarea v-model.trim="form.sustainableDescription" rows="5" maxlength="10000" required />
            </label>
            <label>
              <span>Descrição de Alta Performance</span>
              <textarea v-model.trim="form.highPerformanceDescription" rows="5" maxlength="10000" />
            </label>
          </div>

          <p v-if="formError" class="form-error" role="alert">{{ formError }}</p>

          <footer>
            <button type="button" class="secondary" :disabled="saving" @click="closeEditor">Cancelar</button>
            <button type="submit" class="primary" :disabled="saving">{{ saving ? 'Salvando...' : 'Salvar' }}</button>
          </footer>
        </form>
      </div>
    </main>
  </controller>
</template>

<script setup>
import { computed, onMounted, reactive, ref } from 'vue'
import FormSpinner from '@/components/utilities/FormSpinner.vue'
import UserService from '@/services/user.service'
import ImportedModulesService from '../services/importedModules.service'

const resourceConfig = {
  areas: { singular: 'Área responsável', plural: 'Áreas responsáveis', resource: 'areas' },
  mandalaAreas: { singular: 'Área da Mandala', plural: 'Áreas da Mandala', resource: 'mandala-areas' },
  natures: { singular: 'Natureza', plural: 'Naturezas', resource: 'natures' },
  indicators: { singular: 'Indicador', plural: 'Indicadores', resource: 'indicators' },
  requirements: { singular: 'Requisito', plural: 'Requisitos', resource: 'requirements' }
}

const cycles = ref([])
const areas = ref([])
const mandalaAreas = ref([])
const natures = ref([])
const indicators = ref([])
const requirements = ref([])
const collapsedMandalaAreas = ref({})
const documentEntries = ref([])
let documentKey = 0
const canManage = ref(UserService.filterPermissions('parametrizacaoSGU'))
const hasDirectorPermission = UserService.filterPermissions('preencherAvaliacaoSgu')
const hasCscPermission = UserService.filterPermissions('preencherAvaliacaoSguCsc')
const canViewResponses = UserService.filterPermissions('visualizarRespostasSgu')
const canViewDashboard = UserService.filterPermissions('Dashboard SGU')
const hasEvaluationPermission = hasDirectorPermission || hasCscPermission
const evaluationRoute = hasDirectorPermission ? '/appscripts/sgu/wizard' : '/appscripts/sgu/avaliacao-csc'
const reportRoute = canViewResponses ? '/appscripts/sgu/respostas' : '/appscripts/sgu/relatorio'
const activeTab = ref(hasEvaluationPermission ? 'requirements' : canManage.value ? 'areas' : 'requirements')
const search = ref('')
const showInactive = ref(false)
const cycleId = ref('')
const loading = ref(true)
const loadError = ref('')
const editorOpen = ref(false)
const editingId = ref(null)
const saving = ref(false)
const formError = ref('')
const form = reactive(emptyForm())

const config = computed(() => resourceConfig[activeTab.value])
const singularLabel = computed(() => config.value.singular)
const pluralLabel = computed(() => config.value.plural)
const activeMandalaAreas = computed(() => mandalaAreas.value.filter(item => item.active))
const activeNatures = computed(() => natures.value.filter(item => item.active))
const selectableAreas = computed(() => activeOrSelected(areas.value, form.areaId))
const selectableMandalaAreas = computed(() => activeOrSelected(mandalaAreas.value, form.mandalaAreaId))
const selectableNatures = computed(() => activeOrSelected(natures.value, form.natureId))
const selectableIndicators = computed(() => activeOrSelected(indicators.value, form.indicatorId))
const tabs = computed(() => {
  const availableTabs = [
    tabPayload('areas', 'Áreas responsáveis', areas.value),
    tabPayload('mandalaAreas', 'Áreas da Mandala', mandalaAreas.value),
    tabPayload('natures', 'Naturezas', natures.value),
    tabPayload('indicators', 'Indicadores', indicators.value),
    tabPayload('requirements', 'Requisitos', requirements.value)
  ]

  return canManage.value ? availableTabs : availableTabs.filter(tab => tab.key === 'requirements')
})
const currentItems = computed(() => {
  if (activeTab.value === 'areas') return areas.value
  if (activeTab.value === 'mandalaAreas') return mandalaAreas.value
  if (activeTab.value === 'natures') return natures.value
  if (activeTab.value === 'indicators') return indicators.value
  return requirements.value
})
const filteredItems = computed(() => {
  const term = normalize(search.value)

  return currentItems.value.filter(item => {
    if (!showInactive.value && !item.active) return false
    if (!term) return true

    return [item.code, item.name, item.description, item.mandalaArea, item.nature, item.relatedDocuments, item.basicDescription, item.sustainableDescription, item.highPerformanceDescription]
      .some(value => normalize(value).includes(term))
  })
})
const requirementGroups = computed(() => {
  if (activeTab.value !== 'requirements') return []

  const groups = new Map()
  filteredItems.value.forEach(item => {
    const name = item.mandalaArea || 'Área da Mandala não informada'
    const key = String(item.mandalaAreaId || normalize(name))
    if (!groups.has(key)) groups.set(key, { key, name, items: [] })
    groups.get(key).items.push(item)
  })

  return Array.from(groups.values())
})

onMounted(loadParameterization)

async function loadParameterization () {
  loading.value = true
  loadError.value = ''
  try {
    const response = await ImportedModulesService.getSguParameterization(cycleId.value || undefined)
    cycles.value = response.data?.cycles || []
    cycleId.value = response.data?.selectedCycleId || ''
    areas.value = response.data?.areas || []
    mandalaAreas.value = response.data?.mandalaAreas || []
    natures.value = response.data?.natures || []
    indicators.value = response.data?.indicators || []
    requirements.value = response.data?.requirements || []
    collapsedMandalaAreas.value = {}
    canManage.value = Boolean(response.data?.canManage)
    if (!canManage.value) activeTab.value = 'requirements'
  } catch (error) {
    loadError.value = apiError(error, 'Não foi possível carregar a parametrização do SGU.')
  } finally {
    loading.value = false
  }
}

function selectTab (tab) {
  if (!canManage.value && tab !== 'requirements') return
  activeTab.value = tab
  search.value = ''
}

function openCreate () {
  if (!canManage.value) return
  editingId.value = null
  Object.assign(form, emptyForm(), {
    displayOrder: nextDisplayOrder(),
    cycleId: cycleId.value,
    referenceId: nextReferenceId(),
    mandalaAreaId: activeMandalaAreas.value[0]?.id || '',
    natureId: activeNatures.value[0]?.id || '',
    areaId: null
  })
  documentEntries.value = []
  addDocument()
  formError.value = ''
  editorOpen.value = true
}

function openEdit (item) {
  if (!canManage.value) return
  editingId.value = item.id
  Object.assign(form, emptyForm(), item, {
    areaId: existingOptionId(areas.value, item.areaId),
    indicatorId: existingOptionId(indicators.value, item.indicatorId)
  })
  documentEntries.value = parseDocumentEntries(item.relatedDocuments)
  formError.value = ''
  editorOpen.value = true
}

function closeEditor () {
  if (saving.value) return
  editorOpen.value = false
  formError.value = ''
}

async function save () {
  if (!canManage.value) return
  saving.value = true
  formError.value = ''
  try {
    const payload = payloadForCurrentTab()
    const response = editingId.value
      ? await ImportedModulesService.updateSguParameter(config.value.resource, editingId.value, payload)
      : await ImportedModulesService.createSguParameter(config.value.resource, payload)
    notify('success', response.data?.message || 'Cadastro salvo com sucesso.')
    editorOpen.value = false
    await loadParameterization()
  } catch (error) {
    formError.value = apiError(error, 'Não foi possível salvar o cadastro.')
  } finally {
    saving.value = false
  }
}

async function deactivate (item) {
  if (!canManage.value) return
  const confirmed = window.confirm(`Deseja desativar ${singularLabel.value.toLowerCase()} “${item.name}”?`)
  if (!confirmed) return

  try {
    const response = await ImportedModulesService.deleteSguParameter(config.value.resource, item.id)
    notify('success', response.data?.message || 'Cadastro desativado com sucesso.')
    await loadParameterization()
  } catch (error) {
    notify('error', apiError(error, 'Não foi possível desativar o cadastro.'))
  }
}

function payloadForCurrentTab () {
  const common = {
    code: form.code,
    name: form.name,
    displayOrder: Number(form.displayOrder),
    active: Boolean(form.active)
  }
  if (activeTab.value !== 'requirements' && activeTab.value !== 'indicators') return common
  if (activeTab.value === 'indicators') return { ...common, description: form.description || null }

  return {
    ...common,
    cycleId: Number(form.cycleId),
    referenceId: Number(form.referenceId),
    weight: Number(form.weight),
    requiresSelfAssessmentAttachment: Boolean(form.requiresSelfAssessmentAttachment),
    mandalaAreaId: Number(form.mandalaAreaId),
    natureId: Number(form.natureId),
    areaId: form.areaId ? Number(form.areaId) : null,
    indicatorId: form.indicatorId ? Number(form.indicatorId) : null,
    relatedDocuments: serializeDocumentEntries(),
    basicDescription: form.basicDescription,
    sustainableDescription: form.sustainableDescription,
    highPerformanceDescription: form.highPerformanceDescription || null
  }
}

function emptyForm () {
  return {
    cycleId: '',
    referenceId: 1,
    weight: 1,
    requiresSelfAssessmentAttachment: false,
    areaId: null,
    mandalaAreaId: '',
    natureId: '',
    indicatorId: null,
    code: '',
    name: '',
    description: '',
    relatedDocuments: '',
    basicDescription: '',
    sustainableDescription: '',
    highPerformanceDescription: '',
    displayOrder: 1,
    active: true
  }
}

function nextDisplayOrder () {
  return currentItems.value.reduce((highest, item) => Math.max(highest, Number(item.displayOrder) || 0), 0) + 1
}

function nextReferenceId () {
  if (activeTab.value !== 'requirements') return 1
  return requirements.value.reduce((highest, item) => Math.max(highest, Number(item.referenceId) || 0), 0) + 1
}

function tabPayload (key, label, items) {
  return { key, label, active: items.filter(item => item.active).length, total: items.length }
}

function apiError (error, fallback) {
  const errors = error.response?.data?.errors
  if (errors) {
    const first = Object.values(errors).flat()[0]
    if (first) return first
  }
  return error.response?.data?.message || fallback
}

function normalize (value) {
  return String(value || '').normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase()
}

function relatedDocuments (value) {
  return parseDocumentEntries(value, false).map(document => ({
    label: document.label || document.destination,
    url: isLink(document.destination) ? document.destination : '',
    reference: !isLink(document.destination) ? document.destination : ''
  }))
}

function isLink (value) {
  return /^https?:\/\//i.test(value)
}

function parseDocumentEntries (value, includeKey = true) {
  return String(value || '').split(/\r?\n/).map(line => line.trim()).filter(Boolean).map((line, index) => {
    const separator = line.indexOf(' | ')
    const hasCustomLabel = separator > 0
    const label = hasCustomLabel ? line.slice(0, separator).trim() : (isLink(line) ? `Documento ${index + 1}` : line)
    const destination = hasCustomLabel ? line.slice(separator + 3).trim() : (isLink(line) ? line : '')

    return { key: includeKey ? ++documentKey : index, label, destination }
  })
}

function addDocument () {
  documentEntries.value.push({ key: ++documentKey, label: '', destination: '' })
}

function removeDocument (index) {
  documentEntries.value.splice(index, 1)
}

function serializeDocumentEntries () {
  const documents = documentEntries.value.map((document, index) => {
    const label = String(document.label || '').trim()
    const destination = String(document.destination || '').trim()
    if (!label && !destination) return ''
    if (!label) return destination
    if (!destination) return label
    return `${label || `Documento ${index + 1}`} | ${destination}`
  }).filter(Boolean)

  return documents.length ? documents.join('\n') : null
}

function toggleMandalaArea (key) {
  collapsedMandalaAreas.value = {
    ...collapsedMandalaAreas.value,
    [key]: !collapsedMandalaAreas.value[key]
  }
}

function isMandalaAreaCollapsed (key) {
  return Boolean(collapsedMandalaAreas.value[key])
}

function natureClass (nature) {
  const normalizedNature = normalize(nature)
  if (normalizedNature.includes('conformidade')) return 'compliance'
  if (normalizedNature.includes('gestao')) return 'management'
  return 'neutral'
}

function activeOrSelected (items, selectedId) {
  return items.filter(item => item.active || Number(item.id) === Number(selectedId))
}

function optionLabel (item) {
  return item.active ? item.name : `${item.name} (inativo)`
}

function existingOptionId (items, selectedId) {
  return items.some(item => Number(item.id) === Number(selectedId)) ? selectedId : null
}

function notify (style, message) {
  if (window.toaster?.emit) {
    window.toaster.emit('open', { style, message, floater: true })
  }
}
</script>

<style scoped>
.parameterization-page{background:#f5f6f8;color:#222738;min-height:100vh;padding:24px}.parameterization-header{align-items:flex-end;background:linear-gradient(135deg,#930018,#c0001d);border-radius:12px;color:#fff;display:flex;justify-content:space-between;padding:24px}.parameterization-header a{color:#ffd5dc;font-size:12px}.parameterization-header h1{font-size:24px;margin:8px 0 4px}.parameterization-header p{margin:0;opacity:.9}.parameterization-actions{display:flex;gap:10px}.parameterization-actions a{align-items:center;background:#fff;border-radius:7px;color:#a5001a;display:flex;font-size:13px;font-weight:700;justify-content:center;min-height:40px;min-width:100px;padding:10px 16px;text-decoration:none}.parameterization-actions a.active{background:#09115b;color:#fff}.primary,.state-card button{background:#fff;border:0;border-radius:7px;color:#a5001a;cursor:pointer;font-weight:700;padding:11px 16px}.parameterization-tabs{display:grid;gap:12px;grid-template-columns:repeat(auto-fit,minmax(150px,1fr));margin:16px 0}.parameterization-tabs button{align-items:center;background:#fff;border:1px solid #e0e3e9;border-radius:9px;color:#4c5360;cursor:pointer;display:flex;font-weight:700;justify-content:space-between;padding:15px 18px}.parameterization-tabs button.active{border-color:#c0001d;box-shadow:inset 4px 0 #c0001d;color:#a5001a}.parameterization-tabs b{background:#f1f2f5;border-radius:999px;font-size:11px;padding:4px 8px}.toolbar-card,.list-card,.state-card{background:#fff;border:1px solid #e3e5ea;border-radius:10px;margin-bottom:14px}.toolbar-card{align-items:end;display:grid;gap:14px;grid-template-columns:minmax(150px,1fr) minmax(240px,2fr) auto;padding:15px}.toolbar-card.evaluator-toolbar{grid-template-columns:minmax(240px,2fr) auto}.toolbar-card label,.form-grid label,.full-field,.layer-fields label{display:grid;gap:6px}.toolbar-card label>span,.form-grid label>span,.full-field>span,.layer-fields label>span{color:#606774;font-size:11px;font-weight:700;text-transform:uppercase}.toolbar-card input,.toolbar-card select,.form-grid input,.form-grid select,.full-field textarea,.layer-fields textarea{border:1px solid #d7dae1;border-radius:7px;color:#303441;font:inherit;padding:9px 10px;width:100%}.inactive-filter{align-items:center!important;display:flex!important;height:40px}.inactive-filter input{width:auto}.list-heading{align-items:center;display:flex;justify-content:space-between;padding:17px 18px}.list-heading h2{font-size:18px;margin:0 0 4px}.list-heading p{color:#737986;font-size:12px;margin:0}.table-wrap{overflow:auto}table{border-collapse:collapse;min-width:1050px;width:100%}.requirements-table{min-width:1500px}.requirements-table th:nth-child(1),.requirements-table td:nth-child(1){min-width:210px}.requirements-table th:nth-child(2),.requirements-table td:nth-child(2){min-width:240px}.requirements-table th:nth-child(n+3):nth-child(-n+5),.requirements-table td:nth-child(n+3):nth-child(-n+5){min-width:300px}.requirements-table th:nth-child(6),.requirements-table td:nth-child(6){min-width:180px}th,td{border-top:1px solid #eceef2;padding:12px 14px;text-align:left;vertical-align:top}th{background:#fafbfc;color:#686f7b;font-size:10px;text-transform:uppercase}td strong,td small{display:block}td small{color:#777e89;margin-top:3px}td p{color:#686f7b;font-size:12px;line-height:1.4;margin:7px 0 0;max-width:520px}.description-cell{font-size:12px;line-height:1.45;white-space:pre-wrap}.documents-cell a,.documents-cell span{display:block;font-size:12px;margin-bottom:6px}.documents-cell a{color:#a5001a;font-weight:700}.order-badge{background:#f0f2f5;border-radius:6px;display:inline-block;font-weight:700;min-width:34px;padding:5px;text-align:center}.status-badge{background:#e6f4e9;border-radius:999px;color:#29743a;display:inline-flex;font-size:11px;font-weight:700;margin-top:7px;padding:5px 9px}.status-badge.inactive{background:#eceef1;color:#737985}.actions-column{text-align:right}.row-actions{text-align:right;white-space:nowrap}.row-actions button{background:none;border:0;cursor:pointer;font-weight:700;padding:7px}.row-actions .btn+.btn{margin-left:6px}.edit-action{color:#4a5568}.delete-action{color:#a5001a}.empty-state,.state-card{padding:34px;text-align:center}.state-card.error,.form-error{color:#a5001a}.editor-overlay{align-items:center;background:#11151ccc;display:flex;inset:0;justify-content:center;padding:24px;position:fixed;z-index:3000}.editor-modal{background:#fff;border-radius:12px;max-height:94vh;max-width:980px;overflow:auto;padding:22px;width:100%}.editor-modal>header{align-items:flex-start;display:flex;justify-content:space-between}.editor-modal>header small{color:#a5001a;font-weight:700;text-transform:uppercase}.editor-modal h2{font-size:21px;margin:4px 0 18px}.editor-modal>header button{background:none;border:0;cursor:pointer;font-size:28px}.form-grid{display:grid;gap:13px;grid-template-columns:repeat(2,1fr)}.form-grid input,.form-grid select{height:40px}.form-grid b,.full-field b,.layer-fields b{color:#c0001d}.full-field{margin-top:14px}.full-field textarea,.layer-fields textarea{line-height:1.45;resize:vertical}.layer-fields{display:grid;gap:12px;grid-template-columns:repeat(3,1fr);margin-top:15px}.form-error{background:#fff0f2;border-radius:7px;padding:10px}.editor-modal footer{display:flex;gap:9px;justify-content:flex-end;margin-top:20px}.editor-modal footer button{border:0;border-radius:7px;cursor:pointer;font-weight:700;padding:10px 16px}.editor-modal .primary{background:#a5001a;color:#fff}.secondary{background:#eceef2;color:#343943}button:disabled{cursor:not-allowed;opacity:.6}@media(max-width:800px){.parameterization-page{padding:12px}.parameterization-header{align-items:stretch;display:grid;gap:18px}.parameterization-actions{display:grid;grid-template-columns:repeat(auto-fit,minmax(100px,1fr))}.parameterization-tabs,.form-grid,.layer-fields{grid-template-columns:1fr}.toolbar-card{align-items:stretch;grid-template-columns:1fr}.list-heading{align-items:stretch;gap:14px;flex-direction:column}}
.requirements-table{min-width:1900px}.requirements-table th:nth-child(8),.requirements-table td:nth-child(8){min-width:180px}.mandala-section-row td{background:#f1f3f6;border-top:2px solid #dfe3e9;padding:0}.mandala-section-toggle{align-items:center;background:transparent;border:0;color:#303746;cursor:pointer;display:flex;font:inherit;font-weight:700;gap:8px;padding:12px 14px;text-align:left;width:100%}.mandala-section-toggle>span{color:#a5001a;font-size:10px}.mandala-section-toggle small{background:#fff;border-radius:999px;color:#737986;display:inline-flex;margin:0 0 0 4px;padding:3px 8px}.mandala-area-cell{font-weight:400}.layer-pill,.nature-pill{border-radius:999px;display:inline-flex;font-size:10px;font-weight:700;line-height:1;margin-right:7px;padding:4px 8px;vertical-align:middle;white-space:nowrap}.layer-pill.basic{background:#fff1b8;color:#765900}.layer-pill.sustainable{background:#dff3e4;color:#25683a}.layer-pill.high-performance{background:#dcecff;color:#235b91}.nature-pill{margin-top:7px}.nature-pill.management{background:#eee4ff;color:#674293}.nature-pill.compliance{background:#ffe6d5;color:#8b4d23}.nature-pill.neutral{background:#eceef2;color:#5e6570}.document-editor{border-top:1px solid #eceef2;margin-top:16px;padding-top:16px}.document-editor-heading{align-items:center;display:flex;gap:16px;justify-content:space-between}.document-editor-heading strong,.document-editor-heading small{display:block}.document-editor-heading small{color:#737986;margin-top:4px}.document-editor-list{display:grid;gap:10px;margin-top:14px}.document-editor-row{align-items:end;background:#f8f9fb;border:1px solid #e5e7ec;border-radius:8px;display:grid;gap:12px;grid-template-columns:minmax(180px,1fr) minmax(280px,2fr) auto;padding:12px}.document-editor>p{color:#737986;font-size:12px;margin:12px 0 0}@media(max-width:800px){.document-editor-heading,.document-editor-row{align-items:stretch;display:grid;grid-template-columns:1fr}.document-editor-heading .btn,.document-editor-row .btn{justify-content:center;text-align:center}}
.performance-group-header{background:#f4f5f7;color:#4f5663;font-size:11px;letter-spacing:.04em;text-align:center}.basic-layer-cell{background:oklch(0.975 0.025 95);border:1px solid oklch(0.9 0.03 95);color:oklch(0.4 0.08 90)}.sustainable-layer-cell{background:oklch(0.965 0.025 145);border:1px solid oklch(0.89 0.03 145);color:oklch(0.37 0.08 145)}.high-performance-layer-cell{background:oklch(0.965 0.02 235);border:1px solid oklch(0.88 0.03 235);color:oklch(0.38 0.07 235)}
.requirements-table th:nth-child(2),.requirements-table td:nth-child(2){min-width:170px}.requirements-table th:nth-child(3),.requirements-table td:nth-child(3){min-width:240px}.requirements-table th:nth-child(4),.requirements-table td:nth-child(4){min-width:300px}.requirements-table.requirements-table th:nth-child(7),.requirements-table.requirements-table td:nth-child(7){min-width:180px}
.requirements-table th:nth-child(1),.requirements-table td:nth-child(1){min-width:240px}.requirements-table.requirements-table th:nth-child(3),.requirements-table.requirements-table td:nth-child(3){min-width:180px}
.requirements-table{min-width:1650px}
.requirements-table th{color:#111;font-family:inherit;font-size:13px}
.requirements-table .actions-column{text-align:center}
.requirements-table.requirements-table th:nth-child(1),.requirements-table.requirements-table td:nth-child(1){min-width:260px}
.requirements-table.requirements-table th:nth-child(2),.requirements-table.requirements-table td:nth-child(2){min-width:180px}
.requirements-table.requirements-table th:nth-child(3),.requirements-table.requirements-table td:nth-child(3),.requirements-table.requirements-table th:nth-child(4),.requirements-table.requirements-table td:nth-child(4),.requirements-table.requirements-table th:nth-child(5),.requirements-table.requirements-table td:nth-child(5){min-width:300px}
.requirements-table.requirements-table th:nth-child(6),.requirements-table.requirements-table td:nth-child(6){min-width:180px}
.basic-layer-header,.sustainable-layer-header,.high-performance-layer-header{background:#fafbfc;text-align:center}
.documents-cell a{font-style:italic;font-weight:400}
.documents-cell a,.documents-cell span,.documents-cell small{color:#111}
.mandala-section-toggle{color:#ed1c24;font-family:inherit;font-size:13px}
.mandala-section-toggle>span{color:#ed1c24}
.requirement-reference{color:#ed1c24;margin-right:5px}
.nature-pill.management{background:#fdebecff;color:#ed1c24}
.nature-pill.compliance{background:#e3e7fa;color:#0b1c6b}
.requirements-table.requirements-table th:nth-child(1),.requirements-table.requirements-table td:nth-child(1){min-width:260px}
.requirements-table.requirements-table th:nth-child(2),.requirements-table.requirements-table td:nth-child(2){min-width:240px}
.requirements-table.requirements-table th:nth-child(n+3):nth-child(-n+5),.requirements-table.requirements-table td:nth-child(n+3):nth-child(-n+5){min-width:300px}
.requirements-table.requirements-table th:nth-child(6),.requirements-table.requirements-table td:nth-child(6){min-width:180px}
</style>
